import type { StatDeltaProps } from "@volca/ui/components/ui/stat-tile";
import type {
  IBusinessWeeklyStats,
  IBusinessWeeklyStatsItem,
} from "@/business/business";
import { dayjs } from "@/core/utils/date";

export const PERIOD_WEEKS = 4;

type WeeklyCountKey = Exclude<
  keyof IBusinessWeeklyStatsItem,
  "week_start" | "week_end" | "week_range"
>;

export interface PeriodTotals {
  current: number;
  previous: number;
}

const sum = (weeks: IBusinessWeeklyStats, key: WeeklyCountKey) =>
  weeks.reduce((total, week) => total + (week[key] ?? 0), 0);

/**
 * Totals for the latest `weeks` full weeks and the same span just before it.
 * The week in progress is left out: a few days of it against a full week
 * would read as a drop every Monday.
 */
export const getPeriodTotals = (
  stats: IBusinessWeeklyStats,
  key: WeeklyCountKey,
  weeks = PERIOD_WEEKS,
  now = dayjs(),
): PeriodTotals => {
  const sorted = stats
    .filter((week) => !dayjs(week.week_end).isAfter(now))
    .sort((a, b) => dayjs(a.week_start).diff(dayjs(b.week_start)));
  const current = sorted.slice(-weeks);
  const previous = sorted.slice(-weeks * 2, -weeks);
  return { current: sum(current, key), previous: sum(previous, key) };
};

/**
 * The change in words. A percentage when there is a base to compare with,
 * otherwise the raw difference, since "+∞%" says nothing.
 */
export const getPeriodDelta = ({
  current,
  previous,
}: PeriodTotals): Omit<StatDeltaProps, "className"> => {
  const diff = current - previous;
  if (diff === 0) return { direction: "flat", label: "No change" };

  const direction = diff > 0 ? "up" : "down";
  const sign = diff > 0 ? "+" : "−";
  if (previous === 0) return { direction, label: `${sign}${Math.abs(diff)}` };

  const percent = Math.round((Math.abs(diff) / previous) * 100);
  return { direction, label: `${sign}${percent}%` };
};
